'use client';

import React from 'react';
import styles from './index.module.less';
import Image from 'next/image';
import { useAddAccountModal } from '@/context/AddAccountModalProvider';

type SectionHowCard = {
  title: string;
  desc: string;
  content: string;
};

type SectionHowData = {
  title: string;
  desc: string;
  cards: SectionHowCard[];
};

type SectionWhyCard = {
  buttonText: string;
  imgPath: string;
  title: string;
  desc: string;
};

type SectionWhyData = {
  title: string;
  desc: string;
  cards: SectionWhyCard[];
};

type HomeData = {
  contentInfo: {
    'section-how': SectionHowData;
    'section-why': SectionWhyData;
  };
};

const SectionHow: React.FC<{ data: HomeData; uploadImage: () => void }> = ({ data, uploadImage }) => {
  const { openAddAccountModal } = useAddAccountModal();

  const sectionHowData = data.contentInfo['section-how'];
  const sectionWhyData = data.contentInfo['section-why'];

  const handleConnectAccount = () => {
    openAddAccountModal?.();
  };

  const handleUploadImage = () => {
    uploadImage();
  };

  const ctaHandlers = [handleConnectAccount, handleUploadImage];
  const ctaButtonStyles = [styles.ctaButtonPrimary, styles.ctaButtonSecondary];

  return (
    <section className={styles.sectionHow}>
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.header}>
          <h2 className={styles.title}>{sectionHowData?.title}</h2>
          <p className={styles.desc}>{sectionHowData?.desc}</p>
        </div>

        {/* Content Section */}
        <div className={styles.content}>
          {/* Step Cards */}
          <div className={styles.stepCards}>
            {sectionHowData?.cards?.map((card, index) => (
              <div key={index} className={styles.stepCard}>
                <div className={styles.stepCardContent}>
                  <span className={styles.stepNumber}>{card.title}</span>
                  <h3 className={styles.stepTitle}>{card.desc}</h3>
                  <p className={styles.stepDesc}>{card.content}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className={styles.dividerContainer}>
            <div className={styles.divider}></div>
          </div>

          {/* CTA Section */}
          <div className={styles.ctaSection}>
            <div className={styles.ctaHeader}>
              <span className={styles.ctaLabel}>{sectionWhyData?.desc}</span>
              <h3 className={styles.ctaTitle}>{sectionWhyData?.title}</h3>
            </div>

            {/* CTA Cards */}
            <div className={styles.ctaCards}>
              {sectionWhyData?.cards?.map((card, index) => (
                <div key={index} className={styles.ctaCard}>
                  <div className={styles.ctaIcon}>
                    {card.imgPath && <Image src={card.imgPath} alt={card.title} width={24} height={24} />}
                  </div>
                  <div className={styles.ctaCardContent}>
                    <div className={styles.ctaCardText}>
                      <h4 className={styles.ctaCardTitle}>{card.title}</h4>
                      <p className={styles.ctaCardDesc}>{card.desc}</p>
                    </div>
                    <button className={ctaButtonStyles[index]} onClick={ctaHandlers[index]}>
                      <span>{card.buttonText}</span>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M2.917 7H11.083M11.083 7L7 2.917M11.083 7L7 11.083"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionHow;
